import { useEffect, useState, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import { Banner } from './components/Banner';
import { Header } from './components/Header';
import { Pill } from './components/Pill';
import { FilterModal } from './components/FilterModal';
import { EnquiryForm } from './components/EnquiryForm';
import { Loading } from './components/icons/Loading';
import { SchoolProps } from './types/SchoolProps';
import useSWR from 'swr';
import { fetcher } from './utils/fetcher';
import { SchoolListing } from './components/SchoolListing';
import { SchoolMap } from './components/SchoolMap';
import {
  MapCameraProps,
  MapCameraChangedEvent,
} from '@vis.gl/react-google-maps';
import { FormDataType } from './types/FormDataType';
import { BREAKPOINTS } from "./utils/constants";
import allSuburbs from './utils/suburbs';
import { getLatLngFromGoogleMaps }  from './utils/getLatLngFromGoogleMaps';
import { filterGender, filterEducation } from './utils/findNearbySchools';
import findNearbySchools from './utils/findNearbySchools';  


const INITIAL_CAMERA = {
  center: { lat: -37.8136, lng: 144.9631 },
  zoom: 10  
};

const EMAIL_ADDRESS = `[a-zA-Z0-9+._%-+]{1,256}@[a-zA-Z0-9][a-zA-Z0-9-]{0,64}([.][a-zA-Z0-9][a-zA-Z0-9-]{0,25})`;

function isValidEmail(email: string): boolean {
  return new RegExp(EMAIL_ADDRESS).test(email);
}

function isPostcode(search: string): boolean {
  return (/^\d{4}$/).test(search);
}

function isSuburb(search: string): boolean {
  return allSuburbs.map((suburb) => suburb.toLowerCase()).includes(search);
}

const defaultFormData: FormDataType = {
  interestedIn: {
    generalEnquiry: { value: false, required: false, errorMessage: '' },
    infoPack: { value: false, required: false, errorMessage: '' },
    schoolTour: { value: false, required: false, errorMessage: '' },
  },
  primaryContact: {
    firstName: { value: '', required: true, errorMessage: '' },
    lastName: { value: '', required: true, errorMessage: '' },
    relationship: { value: '', required: false, errorMessage: '' },
    email: { value: '', required: true, errorMessage: '', isValid: isValidEmail, invalidMessage: 'Please enter a valid email address' },
  },
  studentDetails: {
    firstName: { value: '', required: true, errorMessage: '' },
    lastName: { value: '', required: true, errorMessage: '' },
    startingYear: { value: '', required: false, errorMessage: '' },
    additionalInfo: { value: '', required: false, errorMessage: '' },
  }
};


function App() {
  const SHOW_OPEN_DAY_BANNER = true;
  const [isMobile, setIsMobile] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isEnquiryFormOpen, setIsEnquiryFormOpen] = useState(false);
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState(false);

  // Initial state for gender and education levels
  const [selectedGender, setSelectedGender] = useState('All');
  const [selectedEducation, setSelectedEducation] = useState('All');
  // Initial state for Map and List view tabs on mobile
  const [selectedTab, setSelectedTab] = useState<'Map' | 'List'>('Map'); 
  // Initial state for search query
  const [search, setSearch] = useState('');

  // Initial state for schools
  const [filteredSchools, setFilteredSchools] = useState<SchoolProps[]>([]);
  const [allSchools, setAllSchools] = useState<SchoolProps[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<SchoolProps | null>(null);
  const [expandedSchool, setExpandedSchool] = useState<string | null>(null);
  const [noResults, setNoResults] = useState(false);
  const [pinLocation, setPinLocation] = useState<{ lat: number; lng: number } | null>(null);

  const handleToggleExpanded = (schoolId: string) => {
    setExpandedSchool((prev) => (prev === schoolId ? null : schoolId));
  };

  // dynamic list of filters applied that aren't "all"
  const activeFilters = [selectedGender, selectedEducation].filter(
    (filter) => filter !== 'All'
  );

  // Function to handle applying changes
  const applyChanges = (gender: string, education: string) => {
    setSelectedGender(gender);
    setSelectedEducation(education);
    setIsFilterModalOpen(false);
  };

  // Function to handle resetting changes
  const resetChanges = () => {
    setSelectedGender('All');
    setSelectedEducation('All');
    setIsFilterModalOpen(false);
  };

  const toggleTab = (tab: 'List' | 'Map') => {
    return () => {
      setSelectedTab(tab);
    };
  };

  const openEnquiryForm = (school: SchoolProps) => {
    setHasBeenSubmitted(false);
    setSelectedSchool(school);
    setIsEnquiryFormOpen(true);
  };

  const closeEnquiryForm = () => {
    const tab = selectedTab === 'List' ? 'List' : 'Map';
    toggleTab(tab)();
    setIsEnquiryFormOpen(false);
  }

  const closeFilterModal = () => {
    const tab = selectedTab === 'List' ? 'List' : 'Map';
    toggleTab(tab)();
    setIsFilterModalOpen(false);
  };

  const openFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const filterSearch = (search: string, schools: SchoolProps[]) => {

    const lowerCaseSearch = search.toLowerCase();


    const schoolNamesResults: SchoolProps[] = [];
    const suburbPostcodeResults: SchoolProps[] = [];
    const addressPart1Results: SchoolProps[] = [];

    schools.forEach((school) => {
      const schoolName = school.school?.toLowerCase();
      const address = school.address?.toLowerCase();

      // skip if school name or address is missing
      if (!schoolName || !address) return;

      const [addressPart1, suburbPostcode] = address.split(',');

      if (schoolName.includes(lowerCaseSearch)) {
        schoolNamesResults.push(school);
      }
      if (suburbPostcode?.includes(lowerCaseSearch)) {
        suburbPostcodeResults.push(school);
      }
      if (addressPart1?.includes(lowerCaseSearch)) {
        addressPart1Results.push(school);
      }
    });

    const searchResults = new Set([
      ...schoolNamesResults,
      ...suburbPostcodeResults,
      ...addressPart1Results,
    ]);

    return Array.from(searchResults);
  }

  const { data, error, isLoading } = useSWR(
    `${process.env.REACT_APP_API_URL}/CemApi/WebApi/GetSchoolsList`,
    fetcher
  );

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < BREAKPOINTS.MOBILE);
    };
  
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
  
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    // Ensure the initial view is synced with the selectedTab state
    toggleTab(selectedTab)();  
  }, [selectedTab]);

  useEffect(() => {
    if (data) {
      const sortedData = data.sort((a: SchoolProps, b: SchoolProps) => a.school.localeCompare(b.school));
      setAllSchools(sortedData);
      setFilteredSchools(sortedData);
    }
  }, [data]);

  // Filter the data based on the filter state
  const debouncedFilter = useCallback(
    debounce(async (allSchools, selectedGender, selectedEducation, search) => {
      if (!allSchools) return;

      let results = [...allSchools];

      // Filter Gender
      if (selectedGender !== 'All') {
        results = filterGender(selectedGender, results);
      }

      // Filter Education
      if (selectedEducation !== 'All') {
        results = filterEducation(selectedEducation, results);
      }

      // Filter Search
      if (search && search.length > 0) {
        results = filterSearch(search, results);
      }

      // Check if there are no results for a certain search on postcode or suburb
      if (results.length === 0) {
        setNoResults(true);
      } else {
        setNoResults(false);
      }

      if (isPostcode(search) || isSuburb(search)) {
        const locationCoords = await getLatLngFromGoogleMaps(search) as { lat: number; lng: number };
        const nearbySchools = await findNearbySchools(allSchools, selectedGender, selectedEducation, locationCoords);
        results = results.concat(nearbySchools);
        setPinLocation(locationCoords);
      } else {
        setPinLocation(null);
      }

      // Remove duplicates
      results = results.filter((school, index, self) =>
        index === self.findIndex((t) => (
          t.eno === school.eno
        ))
      );

      // Save results to filteredSchools state
      setFilteredSchools(results);

    }, 200),
    [selectedEducation, selectedGender, search]
  );

  useEffect(() => {
    debouncedFilter(allSchools, selectedGender, selectedEducation, search);
  }, [allSchools, selectedGender, selectedEducation, search]);

  const [cameraState, setCameraState] =
    useState<MapCameraProps>(INITIAL_CAMERA);
  const handleCameraChange = useCallback(
    (ev: MapCameraChangedEvent) => setCameraState(ev.detail),
    []
  );

  const schoolListRef = useRef<HTMLDivElement>(null);

  const scrollToListing = (listingRef: React.RefObject<HTMLDivElement>) => {
    if (!schoolListRef.current) return;
    if (!listingRef.current) return;


    // Check if the listing is in view
    const rect = listingRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight - 100;
    const isInView = rect.top >= 0 && rect.bottom <= (windowHeight);
    if (isInView) {
      return
    }

    // listingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const isSmallScreen = window.innerWidth < 768;
    let scrollAmount = 0;
    if (isSmallScreen) {
      scrollAmount = listingRef.current.offsetTop - schoolListRef.current.offsetTop + 200;
    } else {
      scrollAmount = listingRef.current.offsetTop - schoolListRef.current.offsetTop - schoolListRef.current.clientHeight / 2 + listingRef.current.clientHeight / 2;
    }

    if (!isInView) {
      schoolListRef.current.scrollTo({
        top: scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className='relative'>
      <div className="school-list-form relative md:pt-12 md:pl-12 md:pb-12 md:h-dvh flex flex-col md:w-[528px]">
        <div className='relative top-0 bottom-0 left-0 right-0 h-full md:rounded-3xl bg-white md:overflow-hidden max-md:h-dvh md:pb-0 flex flex-col'>
          <div id="school-list-header">
            <FilterModal
              isOpen={isFilterModalOpen}
              onClose={closeFilterModal}
              selectedGender={selectedGender}
              selectedEducation={selectedEducation}
              applyChanges={applyChanges}
              resetChanges={resetChanges}
            />
            <EnquiryForm
              isOpen={isEnquiryFormOpen}
              onClose={() => { closeEnquiryForm() }}
              school={selectedSchool}
              hasBeenSubmitted={hasBeenSubmitted}
              setHasBeenSubmitted={setHasBeenSubmitted}
              defaultFormData={defaultFormData}
            />
            <div className='md:relative'>
              <Header
                openFilterModal={openFilterModal}
                genderFilter={selectedGender}
                educationFilter={selectedEducation}
                search={search}
                setSearch={setSearch}
              />
              {SHOW_OPEN_DAY_BANNER && (
                <Banner>
                  <div className='text-xs font-bold' style={{ fontFamily: 'Arial, sans-serif' }}>
                    <div className='whitespace-nowrap text-[11px]'>Interested in attending an Open Day?</div>
                    <a
                      href='https://www.macs.vic.edu.au/Our-Schools/Choosing-a-School/open-days.aspx'
                      target='_blank'
                      rel='noreferrer'
                      className='inline-block text-white mt-1 !no-underline hover:!no-underline visited:!no-underline focus:!no-underline active:!no-underline'
                      style={{ textDecoration: 'none', fontFamily: 'Arial, sans-serif' }}
                    >
                      Learn more
                    </a>
                  </div>
                </Banner>
              )}
              {activeFilters.length > 0 && (
                <ul className='bg-white border-b border-custom-border py-4 px-8 gap-4 z-10 hidden md:relative md:flex'>
                  {selectedGender !== 'All' && (
                    <Pill
                      label={selectedGender}
                      onClick={() => applyChanges('All', selectedEducation)}
                    />
                  )}
                  {selectedEducation !== 'All' && (
                    <Pill
                      label={selectedEducation}
                      onClick={() => applyChanges(selectedGender, 'All')}
                    />
                  )}
                </ul>
              )}
              <ul className='flex md:hidden'>
              <li
                  className={`w-1/2 py-4 text-center font-bold cursor-pointer hover:bg-alice-blue active:bg-alice-blue ${selectedTab === 'Map' ? 'border-b-2 border-bluestone-light' : ''
                    }`}
                  onClick={toggleTab('Map')}>
                  Map
              </li>
              <li
                  className={`w-1/2 py-4 text-center font-bold cursor-pointer hover:bg-alice-blue active:bg-alice-blue ${selectedTab === 'List' ? 'border-b-2 border-bluestone-light' : ''
                    }`}
                  onClick={toggleTab('List')}>
                  List
                </li>
              </ul>
              {isLoading && (
                <div className='flex p-4 justify-center'>
                  <Loading variant='dark' />
                </div>
              )}
              {error && <div className='text-center pt-4' >Error fetching data</div>}

              {filteredSchools.length !== allSchools.length && (
                <div className='flex p-4 justify-between relative md:z-10 bg-white'>
                  <span className={'text-sm font-bold'}>
                    {filteredSchools.length} Search results
                  </span>
                </div>
              )}
            </div>
          </div>
          {!error &&
            <div
              id='map-sm'
              className={`${selectedTab === 'Map' && isMobile ? '' : 'hidden'} md:hidden overflow-hidden`}
            >
              <SchoolMap
                schools={filteredSchools}
                cameraState={cameraState}
                onCameraChanged={handleCameraChange}
                onEnquire={openEnquiryForm}
                pinLocation={pinLocation}
                search={search}
              />
            </div>
          }

          {filteredSchools.length > 0 ? (
            <div
              id='school-list'
              ref={schoolListRef}
              className={`relative flex-1 min-h-0 pb-28 md:pb-4 overflow-y-auto md:rounded-b-3xl md:z-10 bg-white px-1 md:px-4 ${selectedTab === 'List' || !isMobile ? '' : 'hidden'}`}>
              {filteredSchools
                .map((school: SchoolProps, index: number) => (
                  <SchoolListing
                    school={school}
                    listingType='list'
                    search={search}
                    key={index}
                    onEnquire={openEnquiryForm}
                    scrollToListing={scrollToListing}
                    isExpanded={expandedSchool === school.eno}
                    toggleExpanded={() => handleToggleExpanded(school.eno)}
                  />
                ))}
            </div>
          ) : (
            <div className='flex-1 min-h-0 p-4 flex justify-center relative overflow-y-auto md:rounded-b-3xl md:z-10 bg-white'>
              No schools found.
            </div>
          )}
        </div>
      </div>

      <div
        id='map-md'
        className='hidden md:visible md:block md:absolute top-0 flex flex-column'
      >
        <SchoolMap
          schools={filteredSchools}
          cameraState={cameraState}
          onCameraChanged={handleCameraChange}
          onEnquire={openEnquiryForm}
          pinLocation={pinLocation}
          search={search}
        />
      </div>
    </div>
  );
}

export default App;